import constants from '@/store/wc/constants/wc';
import { componentKeys } from "@/helpers/siteSetings";
import _ from "lodash";
import componentsRegister from "@/helpers/wc/componentsRegister";
import { EmployeeStatus } from "@/helpers/Constants";
import Vue from 'vue';

export default {
  [constants.setSiteResolution](state, payload) {
    state.siteResolution = payload;
  },
  [constants.setAllPagesData](state, pages) {
    const data = {};
    for (const page of pages) {
      data[page.id] = page;
    }
    state.allPagesData = data;
    state.modifiedPageIds = new Set();
    state.deletedPageIds = [];
    state.localNewPages = [];
  },
  [constants.updateLocalPageData](state, { pageId, pageData }) {
    Vue.set(state.allPagesData, pageId, pageData);
    if (!state.modifiedPageIds.has(pageId)) {
      state.modifiedPageIds = new Set([...state.modifiedPageIds, pageId]);
    }
  },
  [constants.markPageDeleted](state, { pageId, deleteStatistics }) {
    const localIdx = state.localNewPages.findIndex(p => p._localId === pageId);
    if (localIdx >= 0) {
      state.localNewPages.splice(localIdx, 1);
    } else {
      state.deletedPageIds = [...state.deletedPageIds, { id: pageId, deleteStatistics: !!deleteStatistics }];
    }
    Vue.delete(state.allPagesData, pageId);
    state.modifiedPageIds = new Set([...state.modifiedPageIds].filter(id => id !== pageId));
  },
  [constants.addLocalPage](state, page) {
    Vue.set(state.allPagesData, page._localId, page);
    state.localNewPages = [...state.localNewPages, page];
  },
  [constants.renameLocalPage](state, { localId, title }) {
    const lp = state.localNewPages.find(p => p._localId === localId);
    if (lp) lp.title = title;
  },
  [constants.patchPageProperties](state, { pageId, properties }) {
    const page = state.allPagesData[pageId];
    if (page) {
      Vue.set(state.allPagesData, pageId, { ...page, ...properties });
    }
  },
  [constants.clearLocalChanges](state) {
    state.modifiedPageIds = new Set();
    state.deletedPageIds = [];
    state.localNewPages = [];
  },
  [constants.saveSiteEditorState](state, payload) {
    state.siteEditorReturnState = payload;
  },
  [constants.clearSiteEditorState](state) {
    state.siteEditorReturnState = null;
  },
  [constants.setPage](state, payload) {
    state.selectedPage = Object.keys(payload).length ? {
      ...payload,
      [componentKeys.components]: payload?.[componentKeys.components]?.map(c => {
        const defaultComponent = componentsRegister.find(el => el.name === c.name);
        c = { ...defaultComponent, ...c };

        if (defaultComponent) {
          // added missed properties into loaded page components
          c[componentKeys.styles] = { ...defaultComponent[componentKeys.styles], ...c[componentKeys.styles] };

          if (!c[componentKeys.styles]) {
            c[componentKeys.styles] = { ...defaultComponent[componentKeys.styles] };
          } else {
            for (const key in c[componentKeys.styles]) {
              // check if default object has component key
              if (!Object.prototype.hasOwnProperty.call(defaultComponent[componentKeys.styles], key)) {
                // remove unused keys
                delete c[componentKeys.styles][key];
              }
            }
          }
        }
        return {
          ...c,
        };
      })
    } : {};
  },
  [constants.setCoefficient](state, payload) {
    state.coefficient = payload;
  },
  [constants.setEmployeeSearchString](state, payload) {
    state.employeeSearchPhrase = payload;
  },
  [constants.setSearchString](state, payload) {
    state.searchPhrase = payload;
  },
  [constants.setEmployees](state, payload) {
    state.employeeList = payload;
  },
  [constants.setAllEmployees](state, payload) {
    for (const item of payload) {
      Object.defineProperties(item, {
        fullName: { get() { return `${this.firstName} ${this.lastName}` } },
        isExternal: { get() { return (this.status & EmployeeStatus.external) > 0 } },
        isActive: {
          get() { return (this.status & EmployeeStatus.active) > 0 },
          set(v) { v ? (this.status |= EmployeeStatus.active) : (this.status &= ~EmployeeStatus.active) }
        },
        isDefault: {
          get() { return (this.status & EmployeeStatus.default) > 0 },
          set(v) { v ? (this.status |= EmployeeStatus.default) : (this.status &= ~EmployeeStatus.default) }
        },
        isBusy: {
          get() { return new Date(this.startDate) < new Date() && new Date(this.endDate) > new Date() }
        },
        isUser: {
          get() { return Boolean(this.userId) },
          set(v) { this.userId = v ? -1 : 0 }
        }
      });
    }
    state.allEmployees = payload;
  },
  [constants.setEmployee](state, payload) {
    state.employeeObj = payload;
  },
  [constants.setFinishRegisterSettings](state, payload) {
    state.finishRegisterSettings = payload;
  },
  [constants.setDefaultEmployees](state, payload) {
    state.defaultEmployeeList = payload;
  },
  [constants.setVisitor](state, payload) {
    state.visitorObj = payload;
  },
  [constants.removeVisitor](state, payload) {
    if (state.visitorObj.attendants?.length > 0) {
      state.visitorObj.attendants = state.visitorObj.attendants.filter(v => v.name !== payload);
    } else if (state.visitorObj.name === payload) {
      state.visitorObj = {};
    }
  },
  [constants.setLocalPageSize](state, payload) {
    state.localPageSize = payload;
  },
  [constants.saveDropinSettings](state, payload) {
    state.dropinObject = payload;
  },
  [constants.validateInput](state, payload) {
    state.validate = { ...state.validate, ...payload };
  },
  [constants.deactivatePageComponents](state, { page, components }) {
    state.selectedPage = {
      ...page,
      [componentKeys.components]: _.cloneDeep(components)
        .map(el => ({
          ...el,
          active: false
        }))
    };
  },
  [constants.activateComponent](state, { page, id }) {
    state.selectedPage = {
      ...page,
      [componentKeys.components]: page?.[componentKeys.components]
        .map((el, index) => {
          el = {
            ...el,
            active: false
          };
          if (el.id === id) {
            el = {
              ...el,
              active: true
            };
          }
          return { ...el };
        })
    };
  },
  [constants.updatePageComponents](state, { page, components }) {
    state.selectedPage = {
      ...page,
      [componentKeys.components]: _.cloneDeep(components)
    };
  },
  [constants.setCompanyQueueLines](state, payload) {
    state.companyQueueLines = payload;
  },
  [constants.setQueueVisitors](state, payload) {
    state.queueVisitors = payload;
  },
  [constants.setJobDepartments](state, payload) {
    state.jobDepartments = payload;
  },
  [constants.setJobDepartment](state, payload) {
    state.jobDepartment = payload;
  },
  [constants.setDepartmentUsers](state, payload) {
    state.departmentUsers = payload;
  },
  [constants.setElementsShifted](state, payload) {
    state.elementsShifted = payload;
  },
  [constants.setInfoSearchCategories](state, payload) {
    state.infoSearchCategories = payload;
  },
  [constants.setInfoSearchCategory](state, payload) {
    state.infoSearchCategory = payload;
  },
  [constants.setInfoSearchSubCategory](state, payload) {
    state.infoSearchSubCategory = payload;
  },
  [constants.graveyard](state, payload) {
    state.graveyard = payload;
  },
  [constants.graveyardGravar](state, payload) {
    state.graveyardGravar = payload;
  },
  [constants.patient](state, payload) {
    state.patient = payload;
  },
  [constants.searchPage](state, payload) {
    state.searchPage = payload;
  },
  [constants.buttonState](state, payload) {
    state.buttonState = { ...state.buttonState, [payload.id + '-' + payload.componentId]: payload.disabled };
  },
  [constants.shouldAcceptAgreement](state, value) {
    state.agreementSettings.shouldAcceptAgreement = value;
  },
  [constants.acceptAgreement](state, value) {
    state.agreementSettings.acceptedAgreement = value;
    state.agreementSettings.showAcceptAgreementPopup = !value;
  },
  [constants.showAcceptAgreementPopup](state, value) {
    state.agreementSettings.showAcceptAgreementPopup = value;
  },
  [constants.saveAfterAgreementHook](state, value) {
    state.agreementSettings.acceptedHook = value;
  },
  actionResult(state, value) {
    state.actionResult = value;
  },
  'setShouldResetUserAppliedState'(state, value) {
    state.shouldResetUserAppliedState = value;
  },
  statisticsVisitors(state, value) {
    state.statisticsVisitors = value;
  },
  statisticsQueue(state, value) {
    const queueNames = value.queues?.find(q => q.queueName) ? value.queues : state.statisticsQueue.queues;
    const nameMap = Object.fromEntries(queueNames?.map(q => [q.id, q.queueName]) ?? []);
    if (!value.queues) value.queues = [];
    if (!value.current) value.current = [];
    state.statisticsQueue = value;
    if (value.queues) value.queues.forEach(q => { q.queueName = nameMap[q.id] || '' });
    if (value.current) value.current.forEach(c => { c.queueName = nameMap[c.queueId] || '' });
  },
  statisticsVisHistory(state, value) {
    state.statisticsVisHistory = value;
  },
  progress(state, value) {
    state.progress = value;
  },
};
